import React from 'react';
import Icon from './AppIcon';

const ReviewHistory = ({ workers = [], application }) => {
  // Extract review history from worker and application data
  const reviewEvents = [];

  // Add application-level review events
  if (application?.reviewed_at && application?.reviewedByUser) {
    reviewEvents.push({
      id: `app-review-${application.id}`,
      type: 'application_review',
      action: 'Application Reviewed',
      actionBy: application.reviewedByUser.full_name || application.reviewedByUser.username,
      timestamp: application.reviewed_at,
      icon: 'FileText',
      iconColor: 'text-blue-500',
      bgColor: 'bg-blue-50',
      details: `Application ${application.status} by CLAB admin`
    });
  }

  // Add worker-level review events
  workers.forEach(worker => {
    if (worker?.approved_at && worker?.approvedByUser) {
      reviewEvents.push({
        id: `worker-approve-${worker.id}`,
        type: 'worker_approval',
        action: `${worker.biodata?.full_name || 'Worker'} Approved`,
        actionBy: worker.approvedByUser.full_name || worker.approvedByUser.username,
        timestamp: worker.approved_at,
        icon: 'CheckCircle',
        iconColor: 'text-green-500',
        bgColor: 'bg-green-50',
        details: `Approved by CLAB admin on behalf of ${application?.employer?.company_name || 'employer'}`
      });
    }

    if (worker?.rejected_at && worker?.rejectedByUser) {
      reviewEvents.push({
        id: `worker-reject-${worker.id}`,
        type: 'worker_rejection',
        action: `${worker.biodata?.full_name || 'Worker'} Rejected`,
        actionBy: worker.rejectedByUser.full_name || worker.rejectedByUser.username,
        timestamp: worker.rejected_at,
        icon: 'XCircle',
        iconColor: 'text-red-500',
        bgColor: 'bg-red-50',
        details: worker.remarks || 'Worker rejected by CLAB admin'
      });
    }
  })

  // Add application creation event
  if (application?.created_at) {
    reviewEvents.push({
      id: `app-created-${application.id}`,
      type: 'application_created',
      action: 'Application Submitted',
      actionBy: application.employer?.company_name || 'Employer',
      timestamp: application.created_at,
      icon: 'Plus',
      iconColor: 'text-gray-500',
      bgColor: 'bg-gray-50',
      details: 'Application submitted for review'
    });
  }

  // Sort events by timestamp (newest first)
  const sortedEvents = reviewEvents.sort((a, b) => {
    const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return timeB - timeA;
  });

  if (sortedEvents.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Icon name="Clock" size={20} className="mr-2 text-gray-500" />
          Review History
        </h3>
        <div className="text-center py-8 text-gray-500">
          <Icon name="Clock" size={48} className="mx-auto mb-3 text-gray-300" />
          <p>No review history available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
        <Icon name="Clock" size={20} className="mr-2 text-gray-500" />
        Review History
      </h3>
      
      <div className="space-y-4">
        {sortedEvents.map((event, index) => (
          <div key={event.id} className="relative">
            {/* Timeline line */}
            {index < sortedEvents.length - 1 && (
              <div className="absolute left-6 top-12 bottom-0 w-px bg-gray-200"></div>
            )}
            
            {/* Event item */}
            <div className="flex items-start space-x-4">
              {/* Icon */}
              <div className={`flex-shrink-0 w-12 h-12 rounded-full ${event.bgColor} flex items-center justify-center`}>
                <Icon name={event.icon} size={20} className={event.iconColor} />
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{event.action}</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      By <span className="font-medium">{event.actionBy}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">
                      {new Date(event.timestamp).toLocaleDateString('en-MY', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(event.timestamp).toLocaleTimeString('en-MY', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                
                {/* Event details */}
                {event.details && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">{event.details}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReviewHistory;
